import { createTheme, alpha, type Shadows } from '@mui/material/styles';
import { type ResponsiveStyleValue } from '@mui/system';
import { type CSSProperties } from 'react';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    // Signal 06 specifies one alert. Severity changes the tint, not the layout.
    soft: false;
    filled: false;
    outlined: false;
  }
}

declare module '@mui/material/styles' {
  interface ThemeOptions {
    template: {
      content: {
        px: ResponsiveStyleValue<number>;
        py: ResponsiveStyleValue<number>;
      };
    };
  }

  interface Theme extends ThemeOptions {}
}

declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
    skeletonDarkBg: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    logo: true;
    kpi: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    kpi: CSSProperties;
  }
  interface TypographyVariantsOptions {
    kpi?: CSSProperties;
  }
}

declare module '@mui/material/Link' {
  interface LinkOwnProps {
    'data-link-tone'?: 'dark';
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    active: true;
    ai: true;
    whatsapp: true;
    golden: true;
  }
  interface ChipPropsSizeOverrides {
    large: true;
    xsmall: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    bordered: true;
  }
  interface ButtonPropsSizeOverrides {
    xsmall: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    ['rounded-info']: true;
    ['rounded-default']: true;
    ['rounded-warning']: true;
    ['rounded-error']: true;
    ['rounded-success']: true;
  }
  interface IconButtonPropsSizeOverrides {
    xsmall: true;
  }
}

const defaultTheme = createTheme();

export const brand = {
  50: '#F3EEFE',
  100: '#E6DCFD',
  200: '#CBB8FB',
  300: '#A98BF6',
  400: '#7A47EE',
  500: '#4A00E2',
  600: '#3D00BC',
  700: '#2A0099',
  800: '#1C0070',
  900: '#110056',
};

/** Signal surface roles; control density and component geometry in their existing owners. */
export const surfaceTokens = {
  page: '#F7F5F9',
  /** Signal "degrau tonal": the app floor, one step below `page` (~1.3:1 against white papers). */
  canvas: '#E4E0EB',
  section: '#F0EDF4',
  subtle: '#FBFAFC',
  paper: '#FFFFFF',
  raised: '#FFFFFF',
  overlay: '#FFFFFF',
  dark: '#110056',
  darkRaised: '#1B0B6A',
  darkElevated: '#22107A',
  softBlue: '#7CAFD1',
  coral: '#FF5A52',
  disabled: '#ECE8F2',
  interactiveHover: 'rgba(17, 0, 86, 0.06)',
  interactivePressed: 'rgba(17, 0, 86, 0.11)',
  interactiveSelected: 'rgba(17, 0, 86, 0.08)',
  borderSubtle: '#E7E3EE',
  borderField: '#D6D0E0',
  borderPaper: 'rgba(17, 0, 86, 0.09)',
  borderNav: '#E2DDEA',
  selectionAccent: '#4A00E2',
  shadowPaper: '0 1px 2px rgba(17, 0, 86, 0.06), 0 10px 26px -10px rgba(17, 0, 86, 0.16)',
  shadowRaised: '0 2px 4px rgba(17, 0, 86, 0.04), 0 18px 38px -12px rgba(17, 0, 86, 0.16)',
  shadowOverlay: '0 4px 10px rgba(17, 0, 86, 0.06), 0 38px 76px -20px rgba(17, 0, 86, 0.28)',
  scrim: 'rgba(17, 0, 86, 0.42)',
  drawerScrim: 'rgba(17, 0, 86, 0.28)',
} as const;

/**
 * Bodyprint Partners direction B, Soft Blue (Signal PD2, chosen 2026-10-15): the shell, the page
 * floor and the sign-in stage are Soft Blue tints, so the portal no longer looks like the Admin.
 * Registered exception in docs/design-system/06-regras-de-uso.md.
 */
export const partnerTokens = {
  softBlue: surfaceTokens.softBlue,
  /** Page floor: Soft Blue at ~12%. Dark Blue reads 16.4:1 on it. */
  page: '#EEF4F9',
  /** Top bar, section tabs and sign-in floor: Soft Blue at ~25%. */
  bar: '#DCEAF4',
  border: '#D3E4F0',
  /** Secondary text on the bar: 6.1:1 on `bar`. */
  ink: '#24597D',
} as const;

export const gray = {
  50: surfaceTokens.subtle,
  75: '#F8F6FA',
  99: surfaceTokens.page, // 100 in grey (material)
  100: surfaceTokens.page,
  150: surfaceTokens.section,
  200: surfaceTokens.borderSubtle,
  300: surfaceTokens.borderField,
  400: '#B9B2C7',
  450: '#958EA6',
  500: '#716C84',
  600: '#5F5A72',
  700: '#403A57',
  800: '#2B2640',
  900: surfaceTokens.dark,
};

export const green = {
  50: '#F2FAF9',
  100: '#E5F3F1',
  200: '#C2E6E2',
  300: '#8CCFC8',
  400: '#01867D',
  500: '#017068',
  600: '#015A54',
  700: '#014540',
  800: '#01302C',
  900: '#001C1A',
};

export const orange = {
  50: '#FFF8EF',
  100: '#FFF3E3',
  200: '#FFE0BC',
  300: '#F7B465',
  400: '#F29A2E',
  500: '#C77412',
  600: '#8A4B00',
  700: '#6B3A00',
  800: '#4D2A00',
  900: '#2E1900',
};

export const red = {
  50: '#FFF5F7',
  100: '#FDECF0',
  200: '#FAD9E1',
  300: '#F0A3B4',
  400: '#C8123C',
  500: '#A30E31',
  600: '#870B28',
  700: '#6B081F',
  800: '#4F0617',
  900: '#33030E',
};

export const purple = {
  50: 'hsl(270, 100%, 98%)',
  100: 'hsl(270, 100%, 95%)',
  200: 'hsl(270, 100%, 90%)',
  300: 'hsl(270, 100%, 80%)',
  400: 'hsl(270, 100%, 70%)',
  500: 'hsl(270, 100%, 60%)',
  600: 'hsl(270, 100%, 50%)',
  700: 'hsl(270, 100%, 40%)',
  800: 'hsl(270, 100%, 30%)',
  900: 'hsl(270, 100%, 20%)',
};

export const yellow = {
  50: 'hsl(40, 100%, 97%)',
  100: 'hsl(40, 100%, 92%)',
  200: 'hsl(40, 100%, 82%)',
  300: 'hsl(40, 100%, 56%)',
  400: 'hsl(40, 100%, 50%)',
  500: 'hsl(40, 100%, 44%)',
  600: 'hsl(40, 100%, 36%)',
  700: 'hsl(40, 100%, 28%)',
  800: 'hsl(40, 100%, 20%)',
  900: 'hsl(40, 100%, 14%)',
};

export const teal = {
  50: 'hsl(180, 70%, 97%)',
  100: 'hsl(180, 65%, 90%)',
  200: 'hsl(180, 60%, 80%)',
  300: 'hsl(180, 55%, 65%)',
  400: 'hsl(180, 55%, 45%)',
  500: 'hsl(180, 65%, 35%)',
  600: 'hsl(180, 70%, 28%)',
  700: 'hsl(180, 75%, 20%)',
  800: 'hsl(180, 80%, 14%)',
  900: 'hsl(180, 85%, 8%)',
};

export const pink = {
  50: 'hsl(330, 90%, 97%)',
  100: 'hsl(330, 85%, 92%)',
  200: 'hsl(330, 80%, 84%)',
  300: 'hsl(330, 75%, 70%)',
  400: 'hsl(330, 70%, 55%)',
  500: 'hsl(330, 75%, 45%)',
  600: 'hsl(330, 80%, 36%)',
  700: 'hsl(330, 85%, 28%)',
  800: 'hsl(330, 85%, 20%)',
  900: 'hsl(330, 85%, 12%)',
};

/** Signal roles for actions, form controls and feedback. */
export const controlTokens = {
  primary: surfaceTokens.dark,
  primaryHover: brand[700],
  primaryPressed: '#0A0035',
  cobalt: brand[500],
  coral: surfaceTokens.coral,
  coralHover: '#FF7770',
  coralPressed: '#E94A42',
  error: red[400],
  errorHover: red[500],
  errorPressed: red[600],
  errorSoft: red[100],
  errorSoftHover: red[200],
  errorBorder: red[300],
  success: green[400],
  successHover: green[500],
  successPressed: green[600],
  successSoft: green[100],
  warning: orange[600],
  warningFill: orange[400],
  warningFillHover: orange[300],
  warningSoft: orange[100],
  info: '#24597D',
  infoFill: '#5C97BF',
  infoFillHover: '#78AACC',
  infoSoft: '#EAF3F9',
  whatsapp: '#25D366',
  disabled: '#B3AEC0',
  focusRing: `0 0 0 2px ${surfaceTokens.paper}, 0 0 0 4px ${brand[500]}`,
  vividRed: '#FF003F',
  signalGradient: `linear-gradient(135deg, #FF003F, ${surfaceTokens.coral})`,
  aiGradient: 'linear-gradient(115deg, #C000FF 0%, #7800EC 100%)',
} as const;

/** Signal 07 media dialog: one 64px crest corner (top right). Pass through `PaperProps.className`. */
export const DIALOG_CRISTA_CORNER_CLASS = 'signal-dialog-corner';

/** Tooltip tones of Signal 07, applied through `slotProps.tooltip.className`. */
export const TOOLTIP_LIGHT_CLASS = 'signal-tooltip-light';
export const TOOLTIP_RICH_CLASS = 'signal-tooltip-rich';

/** Signal board 07 (navigation and reveal): tabs, accordion, tooltip, menu, dialog, drawer and sheet. */
export const revealTokens = {
  searchMark: '#FFF0B3',
  itemHover: '#F4F1F7',
  itemDivider: '#EEEAF3',
  itemDisabled: '#C9C3D4',
  iconTile: surfaceTokens.section,
  tabIndicatorHeight: 3,
  tooltipShadow: '0 10px 24px -8px rgba(17, 0, 86, 0.4)',
  tooltipEnterDelay: 400,
  drawerShadowRight: '-30px 0 60px -30px rgba(17, 0, 86, 0.35)',
  drawerShadowLeft: '30px 0 60px -30px rgba(17, 0, 86, 0.35)',
  sheetShadow: '0 -20px 50px -20px rgba(17, 0, 86, 0.3)',
  sheetHandle: surfaceTokens.borderField,
  /** Sheet and the mobile navigation drawer: radius only on the open side. */
  overlayRadius: 28,
  /** Product detail drawer: 480px, 12px off every edge, radius on all four corners. */
  detailDrawerWidth: 480,
  detailDrawerInset: 12,
  detailDrawerRadius: 24,
  /** Product modal (prancha 13): radius and the xs / sm / md widths. */
  modalRadius: 20,
  modalWidthXs: 420,
  modalWidthSm: 560,
  modalWidthMd: 760,
  menuRadius: 18,
  menuItemRadius: 12,
} as const;

/** Signal board 17 (loading): timings in ms and the colors the loaders share. */
export const loadingTokens = {
  delay: 300,
  minVisible: 600,
  spinOuter: 900,
  spinInner: 1400,
  progressIndeterminate: 1400,
  progressEasing: 'cubic-bezier(.65, .05, .35, 1)',
  skeletonShimmer: 1600,
  brandCycle: 2400,
  brandStagger: 90,
  brandEasing: 'cubic-bezier(.2, .7, .2, 1)',
  track: surfaceTokens.borderSubtle,
  innerArcOpacity: 0.55,
} as const;

export const components = {
  paper: {
    color: surfaceTokens.borderPaper,
  },
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        lighter: surfaceTokens.section,
        light: controlTokens.primaryHover,
        main: controlTokens.primary,
        dark: controlTokens.primaryPressed,
        contrastText: surfaceTokens.paper,
      },
      secondary: {
        lighter: '#FFF0EE',
        light: '#FF8C83',
        main: controlTokens.coral,
        dark: '#DB4842',
        contrastText: surfaceTokens.dark,
      },
      info: {
        light: controlTokens.infoSoft,
        main: controlTokens.infoFill,
        dark: controlTokens.info,
        contrastText: surfaceTokens.dark,
      },
      warning: {
        light: orange[100],
        main: controlTokens.warningFill,
        dark: controlTokens.warning,
      },
      error: {
        light: red[100],
        main: controlTokens.error,
        dark: controlTokens.errorHover,
      },
      success: {
        lighter: green[100],
        light: green[100],
        main: controlTokens.success,
        dark: controlTokens.successHover,
      },
      grey: {
        ...gray,
      },
      divider: surfaceTokens.borderSubtle,
      background: {
        default: partnerTokens.page,
        paper: surfaceTokens.paper,
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        disabled: controlTokens.disabled,
        warning: orange[400],
      },
      action: {
        hover: surfaceTokens.interactiveHover,
        selected: surfaceTokens.interactiveSelected,
        disabled: controlTokens.disabled,
        disabledBackground: surfaceTokens.disabled,
      },
      baseShadow: surfaceTokens.shadowPaper,
      skeletonDarkBg: '#555',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[50],
        lighter: brand[100],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      info: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        lighter: green[200],
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(surfaceTokens.paper, 0.12),
      background: {
        default: surfaceTokens.dark,
        paper: surfaceTokens.darkRaised,
      },
      text: {
        primary: 'hsl(0, 0%, 100%)',
        secondary: gray[400],
      },
      action: {
        hover: alpha(surfaceTokens.paper, 0.06),
        selected: alpha(surfaceTokens.paper, 0.11),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
      skeletonDarkBg: '#555',
    },
  },
};

export const typography = {
  fontFamily: "'Geist Signal', system-ui, sans-serif",
  h1: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(28),
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: '-0.025em',
  },
  h2: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
    lineHeight: 1.25,
  },
  h3: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h4: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h5: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 600,
    lineHeight: 1.35,
  },
  h6: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.35,
  },
  subtitle1: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 500,
  },
  subtitle2: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(13),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 1.5,
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
    lineHeight: 1.4,
  },
  overline: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(11),
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
  button: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
    textTransform: 'none' as const,
  },
  kpi: {
    fontFamily: "'Geist Signal', system-ui, sans-serif",
    fontSize: defaultTheme.typography.pxToRem(40),
    fontWeight: 700,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  },
  logo: {
    fontSize: defaultTheme.typography.pxToRem(22),
    fontWeight: 700,
    fontFamily: "'Geist Bodyprint', system-ui, sans-serif",
  },
};

export const shape = {
  borderRadius: 10,
};

/**
 * Signal radius scale (DS fileira 29, L1). Strings, never numbers: in `sx` a number multiplies
 * `shape.borderRadius` (2.5 = 25px). A child sits at least one step below its parent: drawer
 * `xxl` → group/well `md` → cell `sm`.
 */
export const radiusTokens = {
  xs: '6px',
  sm: '10px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  pill: '999px',
} as const;

// Preserve the 25 MUI slots while mapping them to Signal's three semantic elevations.
export const shadows = defaultTheme.shadows.map((_, index) => {
  if (index === 0) return 'none';
  if (index === 1) return surfaceTokens.shadowPaper;
  if (index === 2) return surfaceTokens.shadowRaised;
  return surfaceTokens.shadowOverlay;
}) as Shadows;
